/**
 * Created by Ivo Mehide <ivo@degeetia.ee> on 04.08.17.
 */

import React from "react";
import PropTypes from "prop-types";
import _ from "underscore";
import BaseResourceContainer from "../BaseResourceContainer";
import Navigation from "../../../Navigation";
import ParamParser from "../../../ParamParser";
import {PageActionStore} from "../../../services/PageAction";
import LocationInfo from "../../../LocationInfo";
import {deepEquals} from "../../../util";
import t from "../../../trans";
import DataProvider from "../../../DataProvider";
import SchemaRegistry from "../../../services/SchemaRegistry";
import * as pageFrames from "../PageFrame";
import {InternalErrorException, NotFoundException} from "../../../Exceptions";
import UiSchema from "../../../UiSchema";
import JsonSchema from "../../../JsonSchema";
import DataManager from "../../../DataManager/DataManager";
import {PageActionActions} from "../../../services/PageAction";
import {NavigationStore} from "../../../services/Navigation";

/**
 * @callback OnActionCallback Callback for processing action execution
 * @param {PageAction} action Action object
 * @returns {boolean} Returns true if action was processed by this method
 */

/**
 * @typedef {Object} BaseControllerParamsProps
 * @property {string} path Current full path (including "/ui" prefix)
 * @property {string} resource Current resource name
 * @property {string} action Current action (one of "list", "new", "view", "edit", "delete")
 * @property {?string} id Current resource ID
 * @property {?Object<string,string>} query Query parameters
 * @property {?string} tab Current tab
 * @property {?string} parentPath Parent resource's full path
 * @property {?string} parentResource Parent resource name
 * @property {?string} parentAction Parent resource's action (one of "new", "view", "edit")
 * @property {?string} parentId Parent resource ID
 * @property {?string} parentField Parent resource field corresponging to this resource
 * @property {?string} parentTab Parent tab
 * @property {?boolean} isModal Should current resource shown in modal window
 */
/**
 * @typedef {Object} BaseControllerProps
 * @property {string} basePath Base path of this controller (including "/ui" prefix)
 * @property {BaseControllerParamsProps} params Location parameters
 * @property {boolean} active If controller is currently active
 * @property {Object} location Location object
 * @property {Object} router Router object
 * @property {Object} route Current route object
 */

/**
 * @typedef {Object} BaseControllerState
 * @property {BaseRestStoreState} resource
 * @property {BaseRestStoreState} parentResource
 * @property {LocaleStoreState} locale
 * @property {UiSchema} schema
 * @property {LocationInfo} locationInfo
 */

/**
 * @extends BaseResourceController
 */
class BaseController extends BaseResourceContainer {
    /**
     *
     * @type {BaseControllerProps}
     */
    static propTypes = {
        basePath: PropTypes.string.isRequired,
        /**
         * @type {BaseControllerParamsProps}
         */
        params: PropTypes.shape({
            path: PropTypes.string.isRequired,
            fieldPath: PropTypes.string.isRequired,
            resource: PropTypes.string.isRequired,
            action: PropTypes.oneOf(["list","new","view","edit","delete"]).isRequired,
            id: PropTypes.string,
            query: PropTypes.object,
            tab: PropTypes.string,
            parentPath: PropTypes.string,
            parentFieldPath: PropTypes.string,
            parentResource: PropTypes.string,
            parentAction: PropTypes.oneOf(["list","new","view","edit","delete"]),
            parentId: PropTypes.string,
            parentField: PropTypes.string,
            parentTab: PropTypes.string,
            isModal: PropTypes.bool
        }).isRequired,
        /**
         * Nav schema for current location
         */
        schema: PropTypes.instanceOf(UiSchema).isRequired,
        /**
         * Nav schema for controller (can reside inside tab)
         */
        controllerSchema: PropTypes.instanceOf(UiSchema).isRequired,
        /**
         * Should component process received actions
         */
        active: PropTypes.bool,
        location: PropTypes.object.isRequired,
        router: PropTypes.object.isRequired,
        route: PropTypes.object.isRequired
    };

    static defaultProps = {
        active: true
    };

    /**
     * @name BaseController#state
     * @type BaseControllerState
     */

    /**
     * @name BaseController#props
     * @type BaseControllerProps
     */

    /**
     *
     * @param props
     * @param storeMap
     */
    constructor(props, storeMap = null) {
        super(props, (storeMap ? _.extend({"parentResource": "parentResource"}, storeMap) : {"parentResource": "parentResource"}));

        //this.recycleStores = true;
        /**
         * @type {LocationInfo}
         */
        this.state.locationInfo = new LocationInfo(props.basePath, props.params.path, props.params.query);
        /**
         * @type {UiSchema} Nav schema
         */
        this.state.schema = this.getRootSchema(this.state.locationInfo);
        this.onComponentAction = this.onComponentAction.bind(this);
        this.onChangeActionStore = this.onChangeActionStore.bind(this);
        this.onCloseModal = this.onCloseModal.bind(this);
    }

    getResourceName(props, store) {
        // let schema = this.getSchema(props);
        if (store === "resource") {
            let resource = props.controllerSchema.getResource();
            if (resource) return resource;
        }
        return super.getResourceName(props, store);
    }

    componentWillMount() {
        super.componentWillMount();
        // Listen for actions store:
        PageActionStore.listen(this.onChangeActionStore);
    }

    componentWillUnmount() {
        PageActionStore.unlisten(this.onChangeActionStore);
        super.componentWillUnmount();
    }

    componentWillReceiveProps(nextProps) {
        super.componentWillReceiveProps(nextProps);
        if (!deepEquals(this.props.params, nextProps.params) || this.props.basePath !== nextProps.basePath) {
            let locationInfo = new LocationInfo(nextProps.basePath, nextProps.params.path, nextProps.params.query);
            this.setState({
                locationInfo: locationInfo,
                schema: this.getRootSchema(locationInfo)
            });
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (!deepEquals(this.state, nextState)) return true;
        if (this.props.basePath !== nextProps.basePath) return true;
        if (!deepEquals(this.props.params, nextProps.params)) return true;
        return false;
    }

    render() {
        if (this.state.modal) {
            return (
                <div>
                    {this.renderComponent()}
                    {this.renderModal()}
                </div>
            );
        } else {
            return this.renderComponent();
        }
    }

    renderComponent() {
        let uischema = this.getUiSchema();

        let Component = this.getComponent(uischema);
        if (!Component) throw new NotFoundException();

        return (
            <Component
                navResource={this.state.locationInfo.getNavResource()}
                navField={this.getNavField()}
                basePath={this.props.basePath}
                params={this.props.params}
                breadcrumb={this.getBreadcrumb()}
                actions={this.getActions(uischema)}
                onAction={this.onComponentAction}
                location={this.props.location}
                router={this.props.router}
                route={this.props.route}
                options={uischema.getOptions()}
            />
        );
    }

    renderModal() {
        if (!this.state.modal) return null;
        let Modal = this.state.modal;
        return <Modal onClose={this.onCloseModal}/>;
    }

    openModal(modal) {
        this.setState({modal: modal});
    }

    onCloseModal(success) {
        this.setState({modal: null});
    }

    loadData() {
    }

    getActions(uischema) {
        let actions = uischema.getAction();
        if (actions != null) {
            return _.map(actions, function (a) {
                let result = _.extend({}, a, {params: this.props.params, data: this.getActionData(a)});
                if (this.state.resource.editError && result.action === "save") result.btnClass += " " + "disabled";
                return result;
            }.bind(this));
        }
        return null;
    }

    /**
     * Overload in child class in order to attach data to actions
     * @param a
     * @returns {null}
     */
    getActionData(a) {
        return null;
    }

    /**
     *
     * @param {PageAction} action
     */
    onComponentAction(action) {
        let result = this.onAction(action);
        if (!result) PageActionActions.callAction(action);
    }
    /**
     * Overload on child classes, but call parent method if action was not processed in child method!
     * @param {PageAction} action
     * @returns {boolean} Return true if action was processed by this method. Avoids default processing by parent class
     */
    onAction(action) {
        // If controller is not currently active then we do not process actions!
        if (!this.props.active) return;

        switch (action.action) {

            case "link":
                let paramParser = new ParamParser(action.params, action.data);
                let url = paramParser.formatUrl(action.href);
                if (!url.startsWith("/")) {
                    // Include parent path into URL:
                    url = this.props.params.path + "/" + url;
                    Navigation.push(url);
                } else {
                    Navigation.gotoUiUrl(url);
                }
                return true;

            case "back":
                Navigation.gotoBackOrUp();
                return true;
            case "refresh":
                this.loadData();
                break;
            case "submit":
                // Map to an action having isPrimary set:
                let actions = this.getActions(this.getUiSchema());
                if (actions) {
                    let action = _.find(actions, function (a) {
                        return a.isPrimary && a.action !== "submit";
                    });
                    if (action) return this.onAction(action);
                }
                break;
            default:
                break;
        }
        return false;
    }

    /**
     * Called when REST action executed by the method callRestAction() is finished
     * @abstract
     * @param {PageAction} action
     * @param {RestAction} restAction
     * @param {BaseRestStoreState} state
     * @param {boolean} failed
     * @returns {boolean} Return true if action was processed by this method. Avoids default processing by parent class
     */
    onActionFinished(action, restAction, failed) {
        return false;
    }

    /**
     * Used for executing REST actions. Result of the REST action is returned by the method onActionFinished()
     * @param {PageAction} action
     * @param {RestAction} restAction
     * @param {boolean} defer Set true to run REST action after current processing finishes
     */
    callRestAction(action, restAction, defer=false) {
        restAction.data = action;
        if (action.params.resource !== this.getResourceName(this.props, "resource")) throw new InternalErrorException("Internal error");

        let restActionHandler = this.actions();
        if (defer) restActionHandler.actionStart.defer(restAction);
        else restActionHandler.actionStart(restAction);
    }

    /**
     * Breadcrumb in the form:
     *  Resource Type
     *  Resource Type > New
     *  Resource Type > Resource Name
     *  Resource Type > Resource Name > Edit
     * @return {[null]}
     */
    getBreadcrumb() {

        let locationStack = this.state.locationInfo.getLocationStack();

        let breadcrumb = [];
        let i = 0;
        for (let s of locationStack) {
            let title = null;
            if (s.action) title = this.getTitleForLocationParams(s, i);
            else title = t("Home");
            breadcrumb.push({
                to: s.path,
                title: title
            });
            i++;
        }

        return breadcrumb;
    }

    getTitleForLocationParams(params, i) {
        // let field = this.state.locationInfo.makeResourceFieldPath(params);
        let field = this.state.locationInfo.getResourceFieldPath(i);

        // Title precedence:
        // 1. From Nav schema if present
        let fieldNavSchema = this.state.schema.field(field);
        if (fieldNavSchema.hasTitle()) return fieldNavSchema.getTitle();

        // 2. From JSON Schema + data if action is "list", "view"
        if (params.action === "list" || params.action === "view") {
            let currentResource = (params.parentResource ? params.parentResource : params.resource);
            let jsonSchema = null;
            if (fieldNavSchema.getTitleField()) jsonSchema = fieldNavSchema;
            else jsonSchema = SchemaRegistry.getSchemaObject(params.parentResource, params.resource, params.action);
            let data = (params.action === "list" ? this.state.resource.data : this.state.resource.currentRecord);
            if (currentResource !== this.props.params.resource) data = null;
            let dataProvider = new DataProvider(jsonSchema, data);
            if (params.parentField) dataProvider = dataProvider.getProvider(params.parentField);
            return dataProvider.getTitle();
        }

        // 3. Generated from field name
        return fieldNavSchema.getTitle();
    }

    /**
     * Returns root Schema for current resource
     * @returns {UiSchema}
     */
    getRootSchema(locationInfo) {
        if (!locationInfo) locationInfo = this.state.locationInfo;
        let resource = locationInfo.getNavResource();
        return SchemaRegistry.getNavigationSchemaObject(resource);
    }

    getNavField() {
        return this.state.locationInfo.getResourceFieldPathWithoutTab();
    }

    /**
     * Returns current component's UI Schema
     * @returns {UiSchema}
     */
    getUiSchema() {
        let schema = this.state.schema;
        if (!schema) return null;
        return schema.field(this.getNavField());
    }

    /**
     * Returns controller nav schema
     */
    getControllerSchema() {
        let schema = this.state.schema;
        if (!schema) return null;
        return schema.field(this.state.locationInfo.getResourceFieldPath());
    }

    /**
     *
     * @param {UiSchema} uischema
     * @returns {React.Component}
     */
    getComponent(uischema) {
        if (!uischema) return null;
        let componentName = uischema.getPage();
        if (!componentName) {
            if (this.props.params.isModal) componentName = "Modal";
            else componentName = "Simple";
        }
        if (_.has(pageFrames, componentName)) return pageFrames[componentName];
        return null;
    }

    loadingFinished(key, newState, oldState) {
        let action = newState.action;

        if (action.data && action.data.action) {
            // Called by page action. Process it:
            this.onActionFinished(action.data, action, false);
        }
    }

    /**
     * Called when specified store fails to load data.
     * By default this method uses notification to display error.
     * @param key Store key
     * @param {BaseRestStoreState} newState Store new state
     * @param {BaseRestStoreState} oldState Store old state
     */
    loadingFailed(key, newState, oldState) {
        let action = newState.action;

        if (action.data && action.data.action) {
            // Called by page action. Process it:
            if (this.onActionFinished(action.data, action, true)) return;
        }

        if (action.error && action.error.code === 400 && action.error.reason && action.error.reason.startsWith("resource:")) {
            let errorStr = action.error.reason.substring(9);
            if (errorStr.indexOf("=") >= 0) {
                let errors = errorStr.split(":");
                let errorInfo = {};
                for (let error of errors) {
                    let e = error.split("=");
                    if (e.length == 1) e = [null, e[0]];
                    if (!errorInfo[e[0]]) errorInfo[e] = e[1];
                    else {
                        if (!_.isArray(errorInfo[e[0]])) errorInfo[e[0]].push(e[1]);
                        else errorInfo[e[0]] = [errorInfo[e[0]], e[1]];
                    }
                }
                this.onError(errorInfo);
            }
            super.loadingFailed(key, newState, oldState);
        } else {
            super.loadingFailed(key, newState, oldState);
        }
    }

    /**
     * Called when action returns client-side errors
     * @param {Object} errorInfo
     * @param {RestAction} action
     * @returns {boolean} If returns true then errors will not be sent to the notification store
     */
    onError(errorInfo, action) {
        return false;
    }

    /**
     *
     * @param {PageActionState} state
     */
    onChangeActionStore(state) {
        // Received action. Process it:
        if (state.action) this.onAction(state.action);
    }

    /**
     * Applies change to the current data and returns changed data.
     * @param {Object} oldData Current data to change
     * @param {string} field Field name (can be hierarhical name or null)
     * @param {string} action Data change action, one of: "insert", "update", "delete"
     * @param {Object} newData New modified data
     * @param {func} onError Callback called when there were errors in new data: onError(errorList)
     */
    applyDataChange(oldData, field, action, newData, onError) {
        let dataManager = this.getDataManager();
        let newValue = dataManager.applyFieldChange(
            oldData,
            field,
            action,
            newData);
        if (newValue == null && dataManager.isError() && onError) onError(dataManager.getErrors());
        return newValue;
    }

    /**
     *
     * @return {DataManager}
     */
    getDataManager() {
        let dataHandler = this.getDataHandler();
        let schema = this.getDataSchema();
        return new DataManager(schema, dataHandler);
    }

    /**
     * JSON Schema corresponding to resource.
     * @returns {JsonSchema}
     */
    getDataSchema() {
        return new JsonSchema(SchemaRegistry.getJsonSchema(this.props.params.resource));
    }

    getDataHandler() {
        return this.props.schema.getDataHandler();
    }


}

export default BaseController;
